/**
 * roads.ts — Pure road/traffic geometry (Phaser-free, unit-tested).
 *
 * Computes where a vehicle enters a road, which lane it uses, and how fast/where
 * it moves. Encodes UK left-hand driving so a vehicle keeps to the left of its
 * heading. TrafficManager consumes this to spawn traffic; the geometry is
 * testable in Node, and is the same for every kind of vehicle — a van drives
 * down the same lane as a car.
 */

import type { Rect } from './level';

export type Orient = 'h' | 'v';

/**
 * What kind of way this is.
 *
 * A `street` is tarmac with kerbs and traffic on it. A `lane` is a cobbled
 * pedestrian way — walkable ground, no carriageway, and never a car. The
 * distinction is load-bearing rather than cosmetic: a lane must not be treated
 * as something to keep clear of (it is somewhere you *walk*), must not count as
 * a carriageway crossed when scoring difficulty, and must never appear in a
 * traffic schedule.
 */
export type RoadKind = 'street' | 'lane';

export interface RoadSegment {
  orient: Orient;
  /** y for a horizontal road, x for a vertical road. */
  pos: number;
  /**
   * Where the road starts and ends along its own axis — x for a horizontal
   * road, y for a vertical one.
   *
   * **Both optional, and absent means "the whole map"**, which is what every
   * road was before spans existed. That default is what lets the authored
   * town's `ROADS.SEGMENTS`, and every test written against it, go on reading
   * unchanged. Resolve them through `roadSpan()` rather than reaching for the
   * fields, so the default lives in exactly one place.
   */
  from?: number;
  to?: number;
  /** Absent means `street` — again, what every road was before lanes existed. */
  kind?: RoadKind;
  /**
   * Carriageway width, kerb to kerb. **Absent means the ordinary street width**
   * — what every road was before the town had a high street.
   *
   * Resolve it through `carriagewayWidth()` rather than reading the field, and
   * note the fallback is *passed in*: this module is deliberately free of
   * `config/`, which is exactly why every function here already takes a width
   * rather than importing one. See the note at the top of the file.
   */
  width?: number;
}

export interface WorldSize {
  width: number;
  height: number;
}

/** The map's extent along a road's own axis. */
export function roadExtent(orient: Orient, world: WorldSize): number {
  return orient === 'h' ? world.width : world.height;
}

/**
 * Where a road starts and ends along its own axis, as `[from, to]`.
 *
 * The one place the "absent means the whole map" default is applied. Clamped to
 * the map, and ordered, so a caller never has to defend against a span given
 * backwards or hanging off the edge.
 */
export function roadSpan(seg: RoadSegment, world: WorldSize): [number, number] {
  const extent = roadExtent(seg.orient, world);
  const from = Math.max(0, Math.min(extent, seg.from ?? 0));
  const to = Math.max(0, Math.min(extent, seg.to ?? extent));
  return from <= to ? [from, to] : [to, from];
}

/** Whether this is a carriageway rather than a pedestrian lane. */
export function isStreet(seg: RoadSegment): boolean {
  return (seg.kind ?? 'street') === 'street';
}

/** Whether this is a cobbled pedestrian lane. */
export function isLane(seg: RoadSegment): boolean {
  return !isStreet(seg);
}

/**
 * How wide this road's carriageway is, kerb to kerb.
 *
 * `fallback` is the town's ordinary street width, supplied by the caller
 * because this module knows nothing about `config/`. Every geometry function
 * here already took a width for that reason; they now treat it as the width for
 * roads that do not state one of their own.
 *
 * A **lane** has no carriageway at all, and this still answers for one — the
 * callers that care about the difference ask `isStreet` first, and the ones that
 * do not are asking "how much tarmac is here", to which the honest answer for a
 * cobbled alley is whatever the caller was going to subtract anyway.
 */
export function carriagewayWidth(seg: RoadSegment, fallback: number): number {
  return seg.width ?? fallback;
}

/**
 * The gap each crossing road punches in this road's footway, as spans along
 * this road's own axis.
 *
 * The counterpart to `crossingsOf`, and the reason it is not simply that plus a
 * width: once streets can differ in width, **the gap is the crossing road's
 * width, not this one's**. A footway broken by a wide high street loses more of
 * itself than one broken by a back lane, and computing both from a single
 * number puts paving across a carriageway at every junction between two streets
 * of different sizes.
 */
export function crossingGaps(
  seg: RoadSegment,
  roads: readonly RoadSegment[],
  fallback: number,
  {
    grow = 0,
    ...opts
  }: {
    streetsOnly?: boolean;
    side?: -1 | 1;
    /**
     * Widen every gap by this much on each side.
     *
     * For the footways, where only one orientation may own the corner squares
     * or both draw them and they z-fight — see `footwayGapWidth`, which is the
     * same adjustment stated for a single road.
     */
    grow?: number;
  } = {},
): { from: number; to: number }[] {
  return crossingRoads(seg, roads, opts).map((o) => {
    const half = carriagewayWidth(o, fallback) / 2 + grow;
    return { from: o.pos - half, to: o.pos + half };
  });
}

/**
 * Whether a road runs clean across the map, edge to edge.
 *
 * This is the test for **whether traffic may use it**. A car has to come from
 * somewhere and go somewhere: on a road that stops halfway across town it would
 * appear and vanish in mid-air, which reads far worse than an empty street. So
 * jogged segments, stubs and lanes carry no traffic, and only a through street
 * does — see `planTraffic` in `levelgen/spawns.ts`, which is checked again from
 * scratch by the validator.
 */
export function spansMap(seg: RoadSegment, world: WorldSize): boolean {
  if (!isStreet(seg)) return false;
  const [from, to] = spanBounds(seg);
  return from <= 0 && to >= roadExtent(seg.orient, world);
}

/**
 * A road's span with **no map to clamp it to**: an absent end reads as
 * unbounded rather than as the map edge.
 *
 * That is the right default for every "does this road reach X?" question, and
 * it is why those questions need no `WorldSize` at all — a road that runs to
 * the map edge reaches every position on the map, and so does one that runs to
 * infinity. Use `roadSpan()` instead wherever real coordinates are wanted.
 */
export function spanBounds(seg: RoadSegment): [number, number] {
  const from = seg.from ?? -Infinity;
  const to = seg.to ?? Infinity;
  return from <= to ? [from, to] : [to, from];
}

/**
 * Whether `seg` reaches far enough along its axis to meet a road at `pos`.
 *
 * The question behind every junction, every footway gap and every crossing
 * count once roads can stop partway: two roads at right angles only actually
 * meet if each one's span reaches the other's position.
 */
export function spanReaches(seg: RoadSegment, pos: number): boolean {
  const [from, to] = spanBounds(seg);
  return pos >= from && pos <= to;
}

/**
 * Whether two perpendicular roads genuinely cross, spans and all.
 *
 * Both spans have to reach, not just one. A road running the full width of the
 * map still does not meet a vertical stub that stops before it.
 */
export function roadsMeet(a: RoadSegment, b: RoadSegment): boolean {
  if (a.orient === b.orient) return false;
  return spanReaches(a, b.pos) && spanReaches(b, a.pos);
}

/**
 * Whether `seg` carries on past `pos` on the given side (−1 = the low side of
 * its axis, +1 = the high side).
 *
 * What separates a crossroads from a **T-junction**, seen from the road being
 * crossed. A side road that dies at the high street breaks the footway on the
 * side it arrives from and leaves the far one whole — you can walk straight
 * along the top of a T without stepping off a kerb, and the pavement should
 * show that. Punching the gap on both sides put a notch in the paving opposite
 * a road that was never there.
 */
export function reachesBeyond(seg: RoadSegment, pos: number, side: -1 | 1): boolean {
  const [from, to] = spanBounds(seg);
  return side < 0 ? from < pos : to > pos;
}

/**
 * Where perpendicular roads actually cross `seg`, as positions along its axis.
 *
 * The one derivation behind every gap punched in a footway, every junction
 * square and every break in the tarmac — so that all of them agree about which
 * roads truly meet rather than each filtering the list its own way.
 */
export function crossingsOf(
  seg: RoadSegment,
  roads: readonly RoadSegment[],
  {
    streetsOnly = false,
    side,
  }: {
    streetsOnly?: boolean;
    /**
     * Restrict to roads that carry on past `seg` on this side. Used for the
     * footways, which are per-side: the far pavement of a T-junction is not
     * broken by a road that stops at the near one.
     */
    side?: -1 | 1;
  } = {},
): number[] {
  return crossingRoads(seg, roads, { streetsOnly, side }).map((o) => o.pos);
}

/**
 * The roads that cross this one — the shared filter behind `crossingsOf` and
 * `crossingGaps`.
 *
 * Split out rather than duplicated because the two answer the same question and
 * differ only in what they report about it, and a footway that broke at
 * different places from the junctions it was broken by would be a very quiet
 * bug.
 */
function crossingRoads(
  seg: RoadSegment,
  roads: readonly RoadSegment[],
  { streetsOnly = false, side }: { streetsOnly?: boolean; side?: -1 | 1 } = {},
): RoadSegment[] {
  return roads.filter(
    (o) =>
      (streetsOnly ? isStreet(o) : true) &&
      roadsMeet(seg, o) &&
      (side === undefined || reachesBeyond(o, seg.pos, side)),
  );
}

export interface VehicleSpawn {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Facing angle in radians (0 = pointing +x). */
  angle: number;
}

/**
 * Lane centre offset (perpendicular to the road) for UK left-hand driving.
 * A driver keeps to the LEFT of their travel direction:
 *   - horizontal road: eastbound (dir +1) hugs the north (−y) lane; westbound the south.
 *   - vertical road:   southbound (dir +1) hugs the east (+x) lane; northbound the west.
 * `laneGap` is half a lane (road quarter-width).
 */
export function laneOffset(orient: Orient, dir: 1 | -1, laneGap: number): number {
  if (orient === 'h') return -dir * laneGap; // offset applied to y
  return dir * laneGap; // offset applied to x
}

/**
 * Build the spawn state for a vehicle entering `seg` heading in `dir` (+1 =
 * right/down, −1 = left/up). It starts just off the entering edge (`margin`
 * past it), so `margin` has to clear the longest kind — see
 * `VEHICLES.MAX_LENGTH`.
 */
export function vehicleSpawn(
  seg: RoadSegment,
  dir: 1 | -1,
  world: WorldSize,
  roadWidth: number,
  speed: number,
  margin: number,
): VehicleSpawn {
  // A car drives down the middle of *its own* lane, so the offset comes off the
  // width of the street it is on rather than the town's ordinary one. Get this
  // from the shared width and every vehicle on the high street drives up the
  // gutter while every one on a back street straddles the centre line.
  const laneGap = carriagewayWidth(seg, roadWidth) / 4;
  const offset = laneOffset(seg.orient, dir, laneGap);

  if (seg.orient === 'h') {
    return {
      x: dir === 1 ? -margin : world.width + margin,
      y: seg.pos + offset,
      vx: dir * speed,
      vy: 0,
      angle: dir === 1 ? 0 : Math.PI,
    };
  }
  return {
    x: seg.pos + offset,
    y: dir === 1 ? -margin : world.height + margin,
    vx: 0,
    vy: dir * speed,
    angle: dir === 1 ? Math.PI / 2 : -Math.PI / 2,
  };
}

/**
 * Every carriageway as a full-width world rect, junction squares included.
 *
 * The tarmac a walker has to be *off* — used by the tree clearance rules and by
 * the level generator, both of which need to keep things away from live road
 * rather than draw it. (What gets drawn is `carriagewaySpans()` in
 * `junctions.ts`, which breaks each road at its crossings so no two slabs
 * overlap; that distinction matters for rendering and not at all for clearance.)
 *
 * **Lanes are not in here, and that is the point.** A cobbled lane is ground you
 * are meant to walk down, not a hazard to be kept off — putting it in this list
 * would have the tree rules and the landmark placer treating a footpath as a
 * live road. What a lane needs instead is the *footway* family of rules (nothing
 * solid built across it), which it gets from `footwayBands`.
 */
export function carriagewayRects(
  world: WorldSize,
  segments: readonly RoadSegment[],
  roadWidth: number,
): Rect[] {
  return segments.filter(isStreet).map((seg) => {
    const [from, to] = roadSpan(seg, world);
    const width = carriagewayWidth(seg, roadWidth);
    return seg.orient === 'h'
      ? { x: from, y: seg.pos - width / 2, w: to - from, h: width }
      : { x: seg.pos - width / 2, y: from, w: width, h: to - from };
  });
}

/** True once a vehicle has fully left the map (plus margin) and can be despawned. */
export function isOffMap(x: number, y: number, world: WorldSize, margin: number): boolean {
  return x < -margin || x > world.width + margin || y < -margin || y > world.height + margin;
}
